import React, { useEffect, useState } from "react";
import { GoHomeFill } from "react-icons/go";
import { GrDocumentVerified, GrUserWorker } from "react-icons/gr";
import {
  MdOutlineAccountBalanceWallet,
  MdDescription,
  MdLocalShipping,
  MdEditDocument,
  MdArrowDropDown,
  MdOutlineArrowDropUp,
} from "react-icons/md";
import { useLocation } from "react-router-dom";
import { HiMiniUserGroup } from "react-icons/hi2";
import { BASE_URL } from "../../utils/constant";
import { useAlert } from "../../components/UI_components/Alert";
import "./projects.css";

const Sidebar = ({ setTab, siteId }) => {
  const location = useLocation();
  const { addAlert } = useAlert();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showTeams, setShowTeams] = useState(true);
  const [teams, setTeams] = useState([]);

  const getCurrentTab = () => {
    const pathSegments = location.pathname.split("/");

    // Map URL paths to tab IDs
    const pathToTabMap = {
      home: "Home",
      tasks: "Tasks",
      finance: "Finance",
      materials: "Materials",
      workers: "Workers",
      report: "Report",
      chats: "Chats",
      teams: "Teams",
      details: "Details",
      boq: "BoQ",
      "task-details": "TaskDetails",
      "document-manager": "Document Manager",
    };

    // Find the first segment that matches a known tab path
    for (const segment of pathSegments) {
      if (pathToTabMap[segment]) {
        return pathToTabMap[segment];
      }
    }

    return "Home";
  };

  const selected = getCurrentTab();
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch (error) {
      return null;
    }
  })();

  const getMemberName = (member) => {
    const user = member?.user || {};
    const firstName = member?.first_name || user?.first_name || user?.name || "";
    const lastName = member?.last_name || user?.last_name || "";
    return `${firstName} ${lastName}`.trim() || "Team member";
  };

  const getMemberAvatar = (member) => member?.userpic || member?.user?.userpic;

  const getMemberInitials = (member) => {
    const name = getMemberName(member);
    const initials = name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("");
    return initials || "TM";
  };

  const getMemberRole = (member) => {
    if (member?.joined_as === "owner" || member?.user?.profession === "owner") {
      return "Owner";
    }
    if (member?.is_admin) return "Admin";
    return member?.profession || member?.user?.profession || "Member";
  };

  const getTeamMembers = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const currentSiteId = siteId || localStorage.getItem("site");
      const organization = JSON.parse(localStorage.getItem("organization"));
      const organizationId = organization?.id;

      if (!accessToken || !currentSiteId) return;

      const [teamRes, orgRes] = await Promise.all([
        fetch(`${BASE_URL}/team/list-site-members/${currentSiteId}/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }),
        organizationId
          ? fetch(`${BASE_URL}/org/organization/${organizationId}/members/`, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            })
          : Promise.resolve({
              ok: true,
              json: async () => ({ status: true, data: [] }),
            }),
      ]);

      const teamData = await teamRes.json();
      const orgData = await orgRes.json();

      if (!teamData.status) {
        throw new Error(teamData.message);
      }

      const orgMembersById = Array.isArray(orgData?.data)
        ? orgData.data.reduce((acc, orgMember) => {
            acc[orgMember.id] = orgMember;
            return acc;
          }, {})
        : {};

      const enriched = (teamData.data || []).map((teamMember) => {
        const orgMember = orgMembersById[teamMember.organization_member_id];
        if (!orgMember) return teamMember;
        return {
          ...teamMember,
          user: teamMember.user || orgMember.user,
          profession:
            teamMember.profession || orgMember?.user?.profession || "Member",
        };
      });

      const sorted = enriched.sort((a, b) => {
        const isAYou =
          a.user_id === currentUser?.user ||
          a.user?.email === currentUser?.user_email;
        const isBYou =
          b.user_id === currentUser?.user ||
          b.user?.email === currentUser?.user_email;
        if (isAYou && !isBYou) return -1;
        if (!isAYou && isBYou) return 1;
        return getMemberName(a).localeCompare(getMemberName(b));
      });

      setTeams(sorted);
    } catch (error) {
      addAlert("Error fetching teams", "error");
    }
  };

  useEffect(() => {
    getTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  useEffect(() => {
    const saved = localStorage.getItem("projectSidebarCollapsed");
    if (saved !== null) {
      setIsCollapsed(saved === "true");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "projectSidebarCollapsed",
      isCollapsed ? "true" : "false",
    );
  }, [isCollapsed]);

  return (
    <div
      className={`relative ${isCollapsed ? "w-[80px]" : "w-[230px]"} h-[92vh] pb-2 bg-sm-background pt-2.5`}
      style={
        {
          // overflowY: "scroll",
          // overflowX:"visible"
        }
      }
    >
      <nav
        className={`gap-0.5 flex flex-col h-full pt-2 ${isCollapsed ? "px-2" : "px-2"}`}
        style={{
          overflowY: "auto",
          scrollbarWidth: "none",
        }}
      >
        {[
          { id: "Home", icon: GoHomeFill, label: "Project Dashboard", path: "home" },
          {
            id: "BoQ",
            icon: MdEditDocument,
            label: "BoQ",
            path: "boq",
          },
          {
            id: "Tasks",
            icon: GrDocumentVerified,
            label: "Tasks",
            path: "tasks",
          },
          {
            id: "Materials",
            icon: MdLocalShipping,
            label: "Materials",
            path: "materials",
          },
          { id: "Workers", icon: GrUserWorker, label: "Workers", path: "workers" },
          {
            id: "Finance",
            icon: MdOutlineAccountBalanceWallet,
            label: "Finance",
            path: "finance",
          },
          {
            id: "Report",
            icon: MdDescription,
            label: "Project Report",
            path: "report",
          },
          // {
          //   id: "Document Manager",
          //   icon: MdDescription,
          //   label: "Document Manager",
          //   path: "document-manager",
          // },
        ].map(({ id, icon: Icon, label, path }) => (
          <div
            key={id}
            onClick={() => setTab(path)}
            title={label}
            className={`
              relative group flex items-center ${isCollapsed ? "justify-center " : ""} px-3 py-2.5 text-sm font-medium rounded-lg
              cursor-pointer transition-all duration-200 ease-in-out
              ${selected === id
                ? "text-[#3B0069]"
                : "text-gray-500 hover:text-[#3B0069] hover:bg-purple-50/50"
              }
            `}
          >
            <div
              className={`
                absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8
                rounded-r transition-all duration-200 ease-in-out
                ${selected === id
                  ? "bg-[#3B0069] scale-y-100"
                  : "bg-[#3B0069]/0 scale-y-0 group-hover:bg-[#3B0069]/50 group-hover:scale-y-75"
                }
              `}
            />

            <Icon
              className={`
              w-5 h-5 ${isCollapsed ? "" : "mr-3"} transition-colors duration-200
              ${selected === id
                  ? "text-[#3B0069]"
                  : "text-gray-400 group-hover:text-[#3B0069]"
                }
            `}
            />

            {!isCollapsed && <span className="font-medium">{label}</span>}
          </div>
        ))}
        <div
          onClick={() => setTab("teams")}
          title="Teams"
          className={`
            relative group flex items-center ${isCollapsed ? "justify-center " : "justify-between"} px-3 py-2.5 text-sm font-medium rounded-lg
            cursor-pointer transition-all duration-200 ease-in-out
            ${selected === "Teams"
              ? "text-[#3B0069]"
              : "text-gray-500 hover:text-[#3B0069] hover:bg-purple-50/50"
            }
          `}
        >
          <div
            className={`
              absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8
              rounded-r transition-all duration-200 ease-in-out
              ${selected === "Teams"
                ? "bg-[#3B0069] scale-y-100"
                : "bg-[#3B0069]/0 scale-y-0 group-hover:bg-[#3B0069]/50 group-hover:scale-y-75"
              }
            `}
          />
          <div className="flex items-center">
            <HiMiniUserGroup
              className={`
                w-5 h-5 ${isCollapsed ? "" : "mr-3"} transition-colors duration-200
                ${selected === "Teams"
                  ? "text-[#3B0069]"
                  : "text-gray-400 group-hover:text-[#3B0069]"
                }
              `}
            />
            {!isCollapsed && <span className="font-medium">Teams</span>}
          </div>
          {!isCollapsed && (
            <button
              type="button"
              title={showTeams ? "Hide members" : "Show members"}
              className="rounded p-0.5 text-gray-500 hover:bg-purple-100 hover:text-[#3B0069]"
              onClick={(event) => {
                event.stopPropagation();
                setShowTeams((prev) => !prev);
              }}
            >
              {showTeams ? (
                <MdArrowDropDown className="h-5 w-5" />
              ) : (
                <MdOutlineArrowDropUp className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
        {!isCollapsed && showTeams && (
          <div className="mt-2 flex flex-col gap-2">
            {teams.map((team) => {
              const isYou =
                team?.user_id === currentUser?.user ||
                team?.user?.email === currentUser?.user_email;
              return (
                <div
                  key={team.member_id || team.id}
                  title={getMemberName(team)}
                  onClick={() =>
                    setTab("teams", {
                      memberId:
                        team.member_id ||
                        team.id ||
                        team.organization_member_id ||
                        team.user_id ||
                        team.user?.id,
                    })
                  }
                  className="flex w-full cursor-pointer flex-row items-start justify-between rounded-lg bg-white px-2 py-1 hover:bg-purple-50"
                >
                  <div className="flex min-w-0 flex-row items-center gap-2">
                    {getMemberAvatar(team) ? (
                      <div className="flex h-8 w-8 flex-shrink-0 border-[1.5px] border-sm-secondary items-center justify-center rounded-full bg-[#3B0069]">
                        <img
                          src={getMemberAvatar(team)}
                          alt={getMemberName(team)}
                          className="h-full w-full rounded-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="flex avatar-placeholder h-8 w-8 avatar-placeholder flex-shrink-0 items-center justify-center rounded-full border-[1.5px] border-sm-secondary text-sm font-semibold uppercase text-white">
                        {getMemberInitials(team)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-normal text-[#1C1C1C]">
                        {getMemberName(team)} {isYou ? "(You)" : ""}
                      </p>
                      <p className="truncate text-xs font-normal text-[#737373]">
                        ({getMemberRole(team)})
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </nav>
    </div>
  );
};

export default Sidebar;
